import axios from "axios";

// Sprint 946G — Cross-origin guard.  Emergent's deploy injects
// REACT_APP_BACKEND_URL pointing at its internal `*.emergent.host`
// backend, which is a different origin than the user-facing custom
// domain (e.g. studioplaz.com).  When the frontend is served on the
// custom domain, calls to that internal host go cross-origin under
// `withCredentials: true` + backend `allow_origins=["*"]` — the
// browser rejects the request before it leaves and axios reports
// "Network Error".  Emergent's ingress also routes `/api/*` on every
// frontend host directly to the backend, so falling back to relative
// paths is always safe.  Detect the mismatch at runtime and prefer
// relative paths to avoid the trap.
function _resolveBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
  if (!envUrl) return "";
  if (typeof window === "undefined") return envUrl;
  try {
    const envHost = new URL(envUrl).host.toLowerCase();
    const pageHost = (window.location.host || "").toLowerCase();
    if (envHost && pageHost && envHost !== pageHost) {
      // Cross-origin → use relative paths (Emergent ingress proxies /api/*).
      return "";
    }
    return envUrl;
  } catch {
    return "";
  }
}
const BACKEND_URL = _resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
// Exposed so the /__gate-status diagnostic can show what was actually
// resolved (vs what process.env.REACT_APP_BACKEND_URL contained).
export const RESOLVED_BACKEND_URL = BACKEND_URL;

// Resolve a backend-returned asset URL to a directly-fetchable one.
// Backend returns either:
//   • a fully-qualified URL (R2: https://pub-…r2.dev/…)
//   • a relative path (legacy /api/uploads/…, /api/r2/…, /api/company-branding/logo/…)
// Use this everywhere instead of bare `${API}${url}` template literals.
export function resolveAssetUrl(url) {
  if (!url) return "";
  if (typeof url !== "string") return "";
  if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }
  return `${API}${url.startsWith("/") ? url : `/${url}`}`;
}

// Current language getter — set by LanguageContext on mount/change
let _currentLanguage = "en";
export const setApiLanguage = (lang) => { _currentLanguage = lang; };
export const getApiLanguage = () => _currentLanguage;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Automatically inject language into every request (as query param)
api.interceptors.request.use((config) => {
  const lang = _currentLanguage;
  if (lang && lang !== "en") {
    // Keep the legacy "language" param for endpoints that still rely on it,
    // and also pass "lang" for the marketplace localizer (Sprint 3J-k).
    config.params = { ...config.params, language: lang, lang };
    config.headers = { ...(config.headers || {}), "X-User-Lang": lang };
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      return Promise.reject(err);
    }
    // Feature gating: show upgrade prompt for plan limit / AI quota errors
    const detail = err.response?.data?.detail;
    if (err.response?.status === 403 && detail && typeof detail === "object" && (detail.code === "PLAN_LIMIT_REACHED" || detail.code === "AI_QUOTA_EXCEEDED" || detail.error === "plan_limit_reached" || detail.error === "ai_quota_exceeded")) {
      window.dispatchEvent(new CustomEvent("plan-limit-reached", {
        detail: {
          message: detail.message || "Plan limit reached. Please upgrade.",
          plan: detail.plan,
          resource: detail.resource,
          upgrade_to: detail.upgrade_to,
          upgrade_name: detail.upgrade_name,
          upgrade_price: detail.upgrade_price,
          current: detail.current,
          limit: detail.limit,
        }
      }));
    }
    // Sprint 904 — credit depletion: show buy-credits modal with the
    // backend-recommended top-up package.
    if (
      err.response?.status === 403 && detail && typeof detail === "object" &&
      (detail.code === "INSUFFICIENT_CREDITS" || detail.code === "MONTHLY_LIMIT_EXCEEDED" ||
       detail.error === "insufficient_credits" || detail.error === "monthly_limit_exceeded")
    ) {
      window.dispatchEvent(new CustomEvent("credit-depleted", {
        detail: {
          code: detail.code,
          error: detail.error,
          message: detail.message,
          balance: detail.balance,
          cost: detail.cost,
          deficit: detail.deficit,
          used: detail.used,
          limit: detail.limit,
          plan: detail.plan,
          operation: detail.operation,
          recommended_package: detail.recommended_package,
          all_packages: detail.all_packages,
        }
      }));
    }
    return Promise.reject(err);
  }
);
